/**
 * EmailBodyPreview.tsx — Read-only preview of an email body.
 *
 * Renders the body with the same scoped styles as RichTextEditor
 * (RichTextEditor.css → .outrena-rte-content), so a preview looks exactly
 * like the same email opened in the editor after "Render for All Prospects":
 * paragraph spacing, bullet lists, bold labels and links all show as sent.
 *
 * Unlike RichTextEditor with readOnly, this does not create a Tiptap editor
 * instance — it is a plain styled container, cheap to render.
 *
 * Props:
 *   bodyHtml            — email body, HTML (from the RTE) or legacy plain text
 *   emailSignature      — sender signature; appended like the backend does
 *   physicalAddress     — CAN-SPAM address for the unsubscribe footer
 *   showFooter          — append signature + unsubscribe footer (default false)
 *   highlightVariables  — show {{ variables }} as highlighted chips (default true)
 *   minHeight           — minimum content height in px (default 180)
 *   className           — additional class for the outer wrapper
 */

import "./RichTextEditor.css";

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { buildEmailPreviewHtml } from "@/lib/htmlUtils";

export interface EmailBodyPreviewProps {
  bodyHtml: string | null | undefined;
  emailSignature?: string | null;
  physicalAddress?: string | null;
  showFooter?: boolean;
  highlightVariables?: boolean;
  minHeight?: number;
  className?: string;
}

export function EmailBodyPreview({
  bodyHtml,
  emailSignature,
  physicalAddress,
  showFooter = false,
  highlightVariables = true,
  minHeight = 180,
  className,
}: EmailBodyPreviewProps) {
  const html = useMemo(
    () =>
      buildEmailPreviewHtml({
        bodyHtml,
        emailSignature,
        physicalAddress,
        showFooter,
        highlightVariables,
      }),
    [bodyHtml, emailSignature, physicalAddress, showFooter, highlightVariables],
  );

  return (
    <div className={cn("outrena-rte rounded-md border bg-background", className)}>
      <div className="outrena-rte-content text-sm" style={{ minHeight }}>
        <div
          className="outrena-rte-preview px-4 py-3.5 break-words"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

export default EmailBodyPreview;